import { motion } from "framer-motion";
import { TrendingUp } from "lucide-react";
import CountUp from "@/components/CountUp";

const ease = [0.16, 1, 0.3, 1];

const tiers = [
  {
    label: "Nebenbei",
    detail: "1 Website pro Monat",
    calc: "1 × 2.000€",
    monthEnd: 2000,
    unit: "pro Monat",
    highlight: false,
  },
  {
    label: "Ambitioniert",
    detail: "1 Website pro Woche",
    calc: "4 × 2.000€",
    monthEnd: 8000,
    unit: "pro Monat",
    highlight: false,
  },
  {
    label: "Skaliert",
    detail: "5 Websites pro Woche",
    calc: "5 × 2.000€ = 10.000€ pro Woche",
    monthEnd: 40000,
    unit: "pro Monat",
    highlight: true,
  },
];

export default function IncomeSection() {
  return (
    <section
      className="py-32 md:py-40 px-6 bg-[#0A0A0A]"
      data-testid="income-section"
      style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
    >
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease }}
          className="text-center mb-16"
        >
          <span className="text-[#C9A84C] text-xs font-semibold tracking-[0.3em] uppercase">
            Skalierungs-Szenario
          </span>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#F5F5F7] mt-4 leading-tight tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif", letterSpacing: "-0.02em" }}
          >
            Rechnen wir <span className="text-[#C9A84C]">mal nach.</span>
          </h2>
          <p className="text-[#86868b] text-lg mt-4 max-w-2xl mx-auto">
            Bei durchschnittlich 2.000€ pro Website — was ist möglich, wenn du skalierst?
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {tiers.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease, delay: i * 0.1 }}
              className="relative rounded-2xl p-8 text-center"
              whileHover={{ y: -6, transition: { duration: 0.3, ease } }}
              style={{
                background: t.highlight ? "rgba(201,168,76,0.07)" : "#111111",
                border: t.highlight
                  ? "1px solid rgba(201,168,76,0.4)"
                  : "1px solid rgba(255,255,255,0.08)",
              }}
              data-testid={`income-tier-${i}`}
            >
              {t.highlight && (
                <span
                  className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full"
                  style={{ background: "#C9A84C", color: "#0A0A0A" }}
                >
                  <TrendingUp className="w-3 h-3" />
                  Volles Potenzial
                </span>
              )}
              <p className="text-[#C9A84C] text-xs font-bold tracking-widest uppercase mb-2">
                {t.label}
              </p>
              <p className="text-[#F5F5F7] font-semibold text-base mb-1">{t.detail}</p>
              <p className="text-[#86868b] text-xs mb-6">{t.calc}</p>
              <p
                className={`font-black leading-none ${t.highlight ? "text-5xl sm:text-6xl text-[#C9A84C]" : "text-4xl text-[#F5F5F7]"}`}
                style={t.highlight ? { textShadow: "0 0 30px rgba(201,168,76,0.3)" } : {}}
              >
                <CountUp end={t.monthEnd} suffix="€" duration={2.2} />
              </p>
              <p className="text-[#86868b] text-sm mt-2">{t.unit}</p>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center text-[#86868b]/60 text-xs mt-8 max-w-xl mx-auto"
          data-testid="income-disclaimer"
        >
          Beispielrechnung bei 2.000€ pro Website. Deine Ergebnisse hängen von deinem
          Einsatz, deiner Region und deiner Umsetzung ab — kein Einkommensversprechen.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease, delay: 0.35 }}
          className="text-center mt-10"
        >
          <a
            href="https://payhip.com/order?link=8mes1&pricing_plan=YLBN5OxvzK"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-[#C9A84C] text-[#0A0A0A] font-black text-base px-10 py-4 rounded-full hover:scale-[1.04] transition-transform duration-300"
            style={{ boxShadow: "0 0 24px rgba(201,168,76,0.3)" }}
            data-testid="income-cta-button"
          >
            Jetzt für 299€ starten →
          </a>
          <p className="text-[#86868b]/60 text-xs mt-3">Einmalig · Lebenslanger Zugang · Sofort startklar</p>
        </motion.div>
      </div>
    </section>
  );
}
